// @flow

import get from 'lodash/get';
import { createReducer } from 'redux-act';

import type { ProductData } from '@gandi/cart';
import type {
  CertificatePrice,
  ProductPricesList,
  CertificateDCVMethodOption,
} from '@gandi/redux-resource-price';
import { selectCurrentId } from '@gandi/redux-sharing';
import { createResource, parseApiErrors } from '@gandi/resource/redux';
import type { Resource, FetchResponse } from '@gandi/resource/redux';

import { POST } from 'gandi.redux-fetcher';
import { createLogger } from 'gandi.redux-logger';

import type { FullState, ThunkAction, Dispatch, GetState } from '../../types';
import type {
  CertificateApiResponse,
  CertificateProductError,
  CertificateState,
  DcvParamsAPIPostSucessPayload,
  Phase,
} from '../types';

// -----------------------
// TODO: Should be in `redux-resource-price`

// in case of a renewal, it will be void (<- Why?)
export const selectCertificateCreatePossibilities = (
  state: FullState,
): void | ProductPricesList<CertificatePrice, Phase> =>
  state.gandiReduxResourcePrice?.certificates?.certificate_create;

export const selectCertificateRenewPossibilities = (
  state: FullState,
): void | ProductPricesList<CertificatePrice, Phase> =>
  state.gandiReduxResourcePrice.certificates?.certificate_renew;

// ------------------

// Resource -------------------------------------
const resourceOptions = {
  getConfigOne: (
    certUUID: string,
    sharingId: string,
  ): {
    url: string,
    label: string,
  } => ({
    url: `/certificate/issued-certs/${certUUID}?sharing_id=${sharingId}`,
    label: `certificates_${certUUID}`,
  }),
};

export const certificateResource: Resource<typeof resourceOptions> =
  createResource('certificates', resourceOptions);

const initialState: CertificateState = {
  byId: {},
};
export const certificateReducer: any = createReducer(
  {
    [certificateResource.fetchOne.success.getType()]: (
      state: CertificateState,
      {
        response,
      }: {
        response: FetchResponse<200, CertificateApiResponse>,
      },
    ): CertificateState => ({
      ...state,
      byId: {
        ...state.byId,
        [response.data.uuid]: response.data,
      },
    }),
  },
  initialState,
);

export const selectCertificate = (
  state: FullState,
  uuid: string,
): ?CertificateApiResponse => state.certificate.byId[uuid];

// Actions --------------------------------------

/**
 * Validate CSR to get CN.
 */
/**
 * @deprecated: this mix renew & create
 */
export interface ValidateCSRError extends Error {
  cn?: string;
  certToRenewCN?: string;
}
export type ValidateCSRArgs = {
  csr: string,
};
export type ValidateCSRActionCreator<T> = (
  args: ValidateCSRArgs,
  certToRenew?: CertificateApiResponse,
) => T;
export type ValidateCSRAction = ValidateCSRActionCreator<
  ThunkAction<Promise<DcvParamsAPIPostSucessPayload>>,
>;
export type ValidateCSRDispatchedAction = ValidateCSRActionCreator<
  Promise<any>,
>;

export const validateCSR: ValidateCSRAction =
  (args, certToRenew) => dispatch => {
    const logger = createLogger('CertificateCreate/module/actions');

    return dispatch(
      POST(
        '/certificate/dcv_params',
        { csr: args.csr },
        {
          // $FlowFixMe[incompatible-call]
          on200: (response: FetchResponse<DcvParamsAPIPostSucessPayload>) => {
            if (certToRenew) {
              const cn = get(response, 'data.fqdns.0');
              // in case of renew, the CN extracted from the CSR has to be the same
              // than one of the certificated being renewed
              //  TODO: move this outside of this action
              // TODO: check IDN
              if (cn !== certToRenew.cn) {
                const err = (new Error('CN mismatch'): ValidateCSRError);
                err.cn = cn;
                err.certToRenewCN = certToRenew.cn;
                throw err;
              }
            }
            return response;
          },
          on4XX: (response: FetchResponse<>) => {
            dispatch(
              logger.info({ err: response }, 'Error when validating CSR.'),
            );
            // $FlowFixMe[class-object-subtyping]
            // $FlowFixMe[incompatible-call]
            // $FlowFixMe[incompatible-variance]
            throw parseApiErrors(response);
          },
          on5XX: err => {
            dispatch(logger.error({ err }, 'Error when validating CSR.'));
            const csrError = new Error('Unable to validate the CSR');
            // $FlowFixMe[incompatible-type]
            csrError.stack = err;

            throw csrError;
          },
        },
      ),
    ).then(response => response.data);
  };

/**
 * Validate certificate payload and set errors in the state.
 */

export type ValidateCertificateOptionsOptions = {
  fallbackDCVMethod?: CertificateDCVMethodOption,
};

export type ValidateCertificateOptionsActionCreator<T> = (
  productData: ProductData,
  options?: ValidateCertificateOptionsOptions,
) => T;
export type ValidateCertificateOptionsAction =
  ValidateCertificateOptionsActionCreator<
    ThunkAction<Promise<Array<CertificateProductError>>>,
  >;
export type ValidateCertificateOptionsDispatchedAction =
  ValidateCertificateOptionsActionCreator<
    Promise<Array<CertificateProductError>>,
  >;

/**
 * dry-run
 * internal API call should always return 200 with eventual errors
 */
export const validateCertificateOptions: ValidateCertificateOptionsAction =
  productData => async (dispatch: Dispatch, getState: GetState) => {
    const logger = createLogger('CertificateCreate/module/actions');
    const headers = { 'Dry-Run': 1 };

    const state = getState();
    const sharingId = selectCurrentId(state);

    // If present, it's a renew
    const isRenew =
      productData.payload?.options?.uuid && !productData.payload.migration_uuid;

    const url = isRenew
      ? // $FlowFixMe[incompatible-use]
        // $FlowFixMe[incompatible-type]
        `/certificate/issued-certs/${productData.payload?.options.uuid}?sharing_id=${sharingId}`
      : `/certificate/issued-certs?sharing_id=${sharingId}`;

    const response = await dispatch(
      POST(url, productData.payload, {
        // $FlowFixMe[incompatible-call]
        headers,
        not200: res => {
          return Promise.reject(res);
        },
        on5XX: res => {
          // $FlowFixMe[incompatible-call]
          logger.error(res);
        },
      }),
    );

    const errors = get(response, 'data.errors', []);

    const filteredErrors = errors
      // XXX: legacy, I don't get why we should filter on those errors
      .filter(err => err.name === 'dcv_method' || 'contact.type');

    return filteredErrors;
  };
